// @file Vuex Store module for filtering posts in a wall.
import { checkIfPageIsAccessedByReload, clearSearchParam, getSearchParam, setSearchParam } from 'bits/location'
import { SurfaceFilter } from 'enums'
import type { Module } from 'vuex'
import type { PostFilterState, RootState } from 'vuexstore/surface/types'

const postFilter: Module<PostFilterState, RootState> = {
  namespaced: true,
  state: {
    filter: SurfaceFilter.All,
    xFilterPopover: false,
    isWallAccessedWithFilterParam: false,
  },
  getters: {
    filter: (state: PostFilterState): SurfaceFilter => state.filter,
    isCustomFilter: (state: PostFilterState): boolean => state.filter !== SurfaceFilter.All,
    xFilterPopover: (state: PostFilterState): boolean => state.xFilterPopover,
    xFilterButton: (state: PostFilterState, _getters, _rootState: RootState, rootGetters): boolean => {
      // We only show the filter button if there are submitted posts
      // or if the user navigates to the padlet with a custom filter query parameter.
      return state.isWallAccessedWithFilterParam || rootGetters['post/submittedPostsCount'] > 0
    },
  },
  mutations: {
    SET_FILTER(state: PostFilterState, filter: SurfaceFilter): void {
      state.filter = filter
    },
    SHOW_FILTER_POPOVER(state: PostFilterState): void {
      state.xFilterPopover = true
    },
    HIDE_FILTER_POPOVER(state: PostFilterState): void {
      state.xFilterPopover = false
    },
    SET_WALL_ACCESSED_WITH_FILTER_PARAM(state: PostFilterState, isWallAccessedWithFilterParam: boolean): void {
      state.isWallAccessedWithFilterParam = isWallAccessedWithFilterParam
    },
  },
  actions: {
    initFilter({ commit, rootGetters }): void {
      // Sep 30 2022: We decided to make filter a desktop-only feature
      if (checkIfPageIsAccessedByReload() || rootGetters.isMobileLayout || rootGetters.isApp) {
        commit('SET_FILTER', SurfaceFilter.All)
        clearSearchParam('filter', false)
        return
      }
      const filterSearchParam = getSearchParam('filter') ?? ''
      switch (filterSearchParam.toLowerCase()) {
        case 'published':
          commit('SET_FILTER', SurfaceFilter.Published)
          commit('SET_WALL_ACCESSED_WITH_FILTER_PARAM', true)
          break
        case 'submitted':
          commit('SET_FILTER', SurfaceFilter.Submitted)
          commit('SET_WALL_ACCESSED_WITH_FILTER_PARAM', true)
          break
        default:
          commit('SET_FILTER', SurfaceFilter.All)
          clearSearchParam('filter', false)
          break
      }
    },
    setFilter({ commit }, filter: SurfaceFilter): void {
      commit('SET_FILTER', filter)
      if (filter === SurfaceFilter.All) {
        clearSearchParam('filter', false)
      } else {
        setSearchParam('filter', filter.toLowerCase(), false)
      }
    },
    showFilterPopover({ commit }): void {
      commit('SHOW_FILTER_POPOVER')
    },
    hideFilterPopover({ commit }): void {
      commit('HIDE_FILTER_POPOVER')
    },
  },
}

export default postFilter
